import {closeCurrentDialog, openDeleteDialog, openDialog} from 'platform/components';
import {match} from 'ts-pattern';

import {forwardRef, useImperativeHandle} from 'react';

import {head, isNil} from 'ramda';
import {isArray} from 'ramda-adjunct';

import {
  PatchReceiveNoteBulkEditRequest,
  useBulkDeleteReceiveNoteMutation,
  usePatchReceiveNoteBulkEditMutation,
} from '@dms/api/warehouse';
import i18n from '@dms/i18n';
import {testIds, warehouseRoutes} from '@dms/routes';
import {
  catchUnhandledDataGridActions,
  DmsDataGrid,
  handleApiError,
  logError,
  refreshDmsDatagrid,
} from '@dms/shared';

import {composePath, useNavigate} from 'shared/web';

import type {ActionCallback} from 'features/eag-grid';

import {ReceiveNoteListBulkEdit} from './ReceiveNoteListBulkEdit';

export type ReceiveNoteListHandle = {
  refreshDataGrid: () => void;
};

export const ReceiveNoteList = forwardRef<ReceiveNoteListHandle>((_, ref) => {
  const navigate = useNavigate();
  useImperativeHandle(
    ref,
    () => ({
      refreshDataGrid: () => refreshDmsDatagrid('receive-note'),
    }),
    []
  );

  const [bulkDeleteReceiveNote] = useBulkDeleteReceiveNoteMutation();
  const [bulkEditReceiveNote] = usePatchReceiveNoteBulkEditMutation();

  const handleBulkEditReceiveNote = async (requestBody: PatchReceiveNoteBulkEditRequest) => {
    await bulkEditReceiveNote(requestBody)
      .unwrap()
      .then(closeCurrentDialog)
      .then(() => refreshDmsDatagrid('receive-note'))
      .catch(handleApiError);
  };

  const actionCallback: ActionCallback = ({actionKey, rowId, sourceSearchParams, refreshData}) => {
    const isBulk = isArray(rowId);
    match({actionKey, isBulk})
      .with(
        {actionKey: 'redirectDetail', isBulk: false},
        {actionKey: 'edit', isBulk: false},
        () => {
          const id = isArray(rowId) ? head(rowId) : rowId;

          if (isNil(id)) {
            return logError('Not able to redirect, receiveNoteId is not defined');
          }

          navigate(
            composePath(warehouseRoutes.receiveNoteDetailOverview, {
              queryParams: sourceSearchParams,
              params: {id},
              isSearchParamsPreserved: false,
            })
          );
        }
      )
      .with({actionKey: 'edit', isBulk: true}, () => {
        openDialog(
          <ReceiveNoteListBulkEdit
            rowId={rowId}
            onSubmit={handleBulkEditReceiveNote}
            data-testid={testIds.warehouse.receiveNoteList('dialogs.bulkEdit')}
          />,
          {
            size: 'small',
            title: i18n.t('entity.warehouse.labels.bulkEdit'),
          }
        );
      })
      .with({actionKey: 'delete'}, () => {
        const ids = isArray(rowId) ? rowId : [rowId];

        openDeleteDialog({
          id: 'deleteReceiveNoteDialog',
          text: i18n.t('entity.warehouse.labels.deleteSelectedItemsPrompt'),
          onConfirm: async () =>
            await bulkDeleteReceiveNote({receiveNoteId: ids})
              .unwrap()
              .then(() => refreshData())
              .catch(handleApiError),
          'data-testid': testIds.warehouse.receiveNoteList('deleteReceiveNoteDialog'),
        });
      })
      .with({actionKey: 'corrective'}, () => {
        const id = isArray(rowId) ? head(rowId) : rowId;

        navigate(
          composePath(warehouseRoutes.receiveNoteCorrectionCreate, {
            params: {receiveNoteCreateId: id},
          })
        );
      })
      .otherwise(() => catchUnhandledDataGridActions(actionKey));
  };

  return (
    <DmsDataGrid
      gridCode="receive-note"
      actionCallback={actionCallback}
      data-testid={testIds.warehouse.receiveNoteList('receiveNotePendingList')}
    />
  );
});
